/*  ========================================================================
    # Hooks - Use Get Parameter
    ========================================================================  */

import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Custom hook to get and set URL query parameters with type conversion and validation.
 *
 * @param {string} parameter - The name of the URL query parameter to get and set.
 * @param {'string'|'number'|'boolean'} [type='string'] - The type to which the parameter value should be converted.
 * @param {*} defaultValue - The default value to use if the parameter is not present or invalid.
 * @param {Array<*>} [allowedValues] - An optional array of allowed values. If the parameter value is not in this array, it will be set to the default value.
 * @returns {[*, function]} - Returns an array with the sanitized parameter value and a function to set the parameter value.
 */
export default function useGetParameter(
  parameter,
  type = 'string',
  defaultValue,
  allowedValues = undefined
) {
  const [rawValue, setRawValue] = useState(
    new URLSearchParams(window.location.search).get(parameter)
  );
  const sanitizedValue = useMemo(() => {
    switch (type) {
      case 'number':
        return parseInt(rawValue, 10);
      case 'boolean':
        return rawValue === 'true';
      default:
        return rawValue;
    }
  }, [rawValue, type]);

  const setValue = useCallback(
    newValue => {
      const newParams = new URLSearchParams(window.location.search);
      // If the parameter is the same as the default value, remove it from the URL
      if (newValue === defaultValue) {
        newParams.delete(parameter);
      } else {
        newParams.set(parameter, newValue);
      }
      setRawValue(newValue);
      window.history.replaceState(
        {},
        undefined,
        window.location.pathname + (newParams.size > 0 ? `?${newParams.toString()}` : '')
      );
    },
    [parameter]
  );

  // Correct value if not in allowedValues
  useEffect(() => {
    if (!allowedValues || !Array.isArray(allowedValues)) return;
    if (allowedValues.includes(sanitizedValue)) return;

    // Value is not legit, update to the default value instead
    setValue(defaultValue);
  }, [sanitizedValue, allowedValues, defaultValue]);

  return [sanitizedValue, setValue];
}
